import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Loading, ErrorState } from "../components/states";
import {
  Button,
  Money,
  Chevron,
  Tile,
  PeriodPicker,
  monthLabel,
  Footnote,
  columnsFor,
  plural,
  shortDate,
  SectionHeading,
  SubHeading,
  StatusPill,
} from "../components/kit";
import { useViewer } from "../lib/viewer";
import { useBusyAction } from "../lib/useBusyAction";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { formatCents } from "@shared/money";

/**
 * Monthly statements.
 *
 * The date-range rollup with a monthly total — how Gloria described billing in
 * her original spec, before the later email added per-dispatch invoicing on
 * top. Both exist; this is the one Dee reconciles against at month end.
 *
 * It nearly got dropped once. It is not a nice-to-have.
 *
 * Invoices' sibling, and laid out like it on purpose: a card at 390px, a
 * columned table at `lg`, money in `tabular-nums`, and margin rendered neutral
 * so the only green on the screen still means "this clinic has paid".
 *
 * **One month at a time, and the month is pickable.** This screen used to render
 * whatever `statements.list` happened to return — in practice one row, June,
 * with no picker, no range and nothing saying when July would appear. A manager
 * opening it on the 27th to ask "what have we spent this month" got a June
 * number and no way to tell. Now the period is a control, every month back a
 * year is reachable, and a month whose statement has not been cut yet answers
 * the question anyway from the invoices already issued in it — labelled
 * "month to date" so it is never mistaken for a closed statement.
 *
 * **Spend leads; outstanding follows.** The figure Dee asks for first is what
 * the month cost. What is still owed matters, and it is the second tile.
 *
 * Periods are UTC month boundaries, because that is what `statements.buildMonthEnd`
 * writes — keying them off local time in Vancouver would file the 1st of a month
 * under the previous one.
 */

/** `1753574400000` → `"2026-07"`, in UTC, because a statement's period is a UTC
 *  instant rather than a local one. Exported because Invoices keys its own
 *  month figure off this same boundary. */
export const utcMonthKey = (ms: number): string =>
  new Date(ms).toISOString().slice(0, 7);

const utcMonthStart = (key: string): number => {
  const [year, month] = key.split("-").map(Number);
  return Date.UTC(year, month - 1, 1);
};

const utcMonthEnd = (key: string): number => {
  const [year, month] = key.split("-").map(Number);
  return Date.UTC(year, month, 1);
};

/** The three figures a month is worth, for a month that has not been closed. */
export type MonthTotals = {
  spendCents: number;
  outstandingCents: number;
  invoiceCount: number;
};

/**
 * "What has this month cost so far", defined once for both billing screens.
 *
 * Invoices and Statements answer the same question off the same `invoices.list`
 * payload, and they used to do it against two different month boundaries — one
 * UTC, one local — so on the 1st of a month in Vancouver the two screens, open
 * side by side in the same demo, disagreed. `utcMonthKey` above is the boundary
 * that matches what `statements.buildMonthEnd` writes, so it is the one both
 * screens key off, and this is the only place the rollup is spelled out. One
 * pass, three figures.
 */
export const monthToDate = (
  invoices: readonly {
    issuedAt: number;
    totalCents: number;
    outstandingCents: number;
  }[],
  key: string,
): MonthTotals => {
  let spendCents = 0;
  let outstandingCents = 0;
  let invoiceCount = 0;
  for (const invoice of invoices) {
    if (utcMonthKey(invoice.issuedAt) !== key) continue;
    spendCents += invoice.totalCents;
    outstandingCents += invoice.outstandingCents;
    invoiceCount += 1;
  }
  return { spendCents, outstandingCents, invoiceCount };
};

/**
 * Whole class strings, four of them, for the same reason as Invoices.tsx: a
 * template assembled from variables produces no CSS. Same two axes too — a
 * manager sees one clinic, and margin follows the payload.
 */
const COLUMNS = {
  clinicMargin: "lg:grid-cols-[minmax(0,1fr)_10rem_4rem_7rem_8rem_5.5rem_1.25rem]",
  clinic: "lg:grid-cols-[minmax(0,1fr)_10rem_4rem_7rem_8rem_1.25rem]",
  margin: "lg:grid-cols-[minmax(10rem,1fr)_4rem_7rem_8rem_5.5rem_1.25rem]",
  plain: "lg:grid-cols-[minmax(10rem,1fr)_4rem_7rem_8rem_1.25rem]",
};

export function Statements() {
  const viewer = useViewer();
  const statements = useQuery(api.statements.list, {});
  /* The invoices behind the months that have not been closed yet. Same
     `canSeeBilling` gate as `statements.list`, same clinic scoping, and every
     field read here — `issuedAt`, `totalCents`, `outstandingCents` — is on the
     payload for every role permitted to open this screen at all. Nothing is
     hidden in this component; a role that may not see money never gets here. */
  const invoices = useQuery(api.invoices.list, {});
  const buildMonthEnd = useMutation(api.statements.buildMonthEnd);
  const [openId, setOpenId] = useState<Id<"statements"> | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const { busy, run } = useBusyAction();

  if (statements === undefined || invoices === undefined) {
    return <Loading rows={4} label="Loading statements" />;
  }

  const isOwner = viewer.role === "owner";

  /* Which months are offered. Every month that has a statement, every month
     that has an invoice in it, and always the current one — so the manager
     asking "this month" on the 27th finds it rather than concluding the screen
     only knows about June. Newest first; the current month is the default. */
  const months = new Set<string>([utcMonthKey(Date.now())]);
  for (const statement of statements) months.add(utcMonthKey(statement.periodStart));
  for (const invoice of invoices) months.add(utcMonthKey(invoice.issuedAt));
  const options = [...months]
    .sort((a, b) => b.localeCompare(a))
    .slice(0, 24)
    .map((key) => ({ value: key, label: monthLabel(key) }));

  const selected =
    period && options.some((option) => option.value === period)
      ? period
      : options[0].value;
  const selectedLabel = monthLabel(selected);
  const isCurrentMonth = selected === utcMonthKey(Date.now());

  const runMonthEnd = () =>
    run(
      // The month on screen, not an implicit "last month" — pressing a button
      // labelled "Close July" and getting June's statements is how a month-end
      // gets run twice.
      () =>
        buildMonthEnd({
          periodStart: utcMonthStart(selected),
          periodEnd: utcMonthEnd(selected),
        }),
      {
        failed: `Could not close ${selectedLabel}`,
        done: (result) => ({
          happened: `${selectedLabel} closed — ${formatCents(
            result.totalCents,
          )} across all clinics`,
          next: `${result.built} ${plural(result.built, "statement")} issued, ${
            result.replaced
          } refreshed. They are on this screen now.`,
        }),
      },
    );

  const rows = [...statements]
    .filter((statement) => utcMonthKey(statement.periodStart) === selected)
    /* Debt first inside the month, biggest first. At month end Dee is looking
       for who has not paid, and alphabetical-by-accident does not answer that. */
    .sort((a, b) => b.outstandingCents - a.outstandingCents);

  /* Spend is the number she came for. When the month is closed it is the sum of
     its statements; when it is not, it is what has been invoiced in it so far,
     said in those words.

     One branch, not three. The three figures used to each re-ask `closed` and
     reduce over one of two arrays, which is three chances to sum the wrong one:
     the source is chosen once here and summed once below. */
  const closed = rows.length > 0;
  const { spendCents: spend, outstandingCents: outstanding, invoiceCount } = closed
    ? rows.reduce<MonthTotals>(
        (total, statement) => ({
          spendCents: total.spendCents + statement.monthlyTotalCents,
          outstandingCents: total.outstandingCents + statement.outstandingCents,
          invoiceCount: total.invoiceCount + statement.invoiceCount,
        }),
        { spendCents: 0, outstandingCents: 0, invoiceCount: 0 },
      )
    : monthToDate(invoices, selected);

  // Same rule as Invoices: the column exists because the payload has margins in
  // it, not because this component decided the viewer is allowed one.
  const showMargin = statements.some((statement) => statement.marginCents !== null);
  const showClinic = !viewer.clinicName;
  const columns = columnsFor(COLUMNS, showClinic, showMargin);

  return (
    <div className="space-y-5 pb-8">
      <SectionHeading
        title="Statements"
        subtitle={`${viewer.clinicName ?? "All clinics"} · one page per clinic per month`}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <Tile
          size="hero"
          label={
            closed ? `Spent in ${selectedLabel}` : `Spent in ${selectedLabel} so far`
          }
          value={formatCents(spend)}
          hint={
            closed
              ? `${invoiceCount} ${plural(invoiceCount, "invoice")} across ${
                  rows.length
                } ${plural(rows.length, "statement")}`
              : `${invoiceCount} ${plural(
                  invoiceCount,
                  "invoice",
                )} issued so far — month to date, not a closed statement`
          }
          aside={
            <PeriodPicker
              value={selected}
              onChange={setPeriod}
              options={options}
              label="Which month"
            />
          }
        />
        <Tile
          label={closed ? "Still outstanding" : "Outstanding on those invoices"}
          value={formatCents(outstanding)}
          tone={outstanding > 0 ? "red" : "neutral"}
          hint={
            outstanding > 0
              ? "Chased on the Invoices screen."
              : "Everything invoiced in this period is paid."
          }
        />
      </div>

      {!closed ? (
        /* `block gap-0 p-4` is the shim every card in this app applies to the
           registry card: it is built around `CardHeader`/`CardContent` slots
           this one does not use, and its children lay themselves out. */
        <Card className="block gap-0 p-4">
          <h2 className="text-sm font-semibold">
            {selectedLabel} has not been closed yet
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {isCurrentMonth
              ? `The month is still running. A statement per clinic is issued when ${selectedLabel} ends; the figure above is what has been invoiced so far.`
              : `No statements were issued for ${selectedLabel}.`}
          </p>
          {isOwner ? (
            <div className="mt-3">
              <Button onClick={runMonthEnd} disabled={busy}>
                {busy ? "Closing…" : `Close ${selectedLabel} now`}
              </Button>
              <Footnote className="mt-2">
                Rolls every clinic's {selectedLabel} invoices into one statement
                with a monthly total. Safe to run twice — it replaces rather than
                duplicates.
              </Footnote>
            </div>
          ) : null}
        </Card>
      ) : (
        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <SubHeading>{selectedLabel} · by clinic</SubHeading>
            {isOwner ? (
              <Button variant="ghost" onClick={runMonthEnd} disabled={busy}>
                {busy ? "Rebuilding…" : "Rebuild"}
              </Button>
            ) : null}
          </div>

          {/* The column header sits with the rows it labels. */}
          <div
            className={cn(
              "hidden px-4 text-xs font-medium tracking-wide text-muted-foreground uppercase",
              columns,
            )}
          >
            {showClinic ? <span>Clinic</span> : null}
            <span>Statement</span>
            <span className="text-right">Invoices</span>
            <span className="text-right">Total</span>
            <span className="text-right">Outstanding</span>
            {showMargin ? <span className="text-right">Margin</span> : null}
            <span />
          </div>

          {rows.map((statement) =>
            openId === statement._id ? (
              <StatementDetail
                key={statement._id}
                statementId={statement._id}
                onClose={() => setOpenId(null)}
              />
            ) : (
              /* A raw <button> wearing the registry's card tokens, for the
                 same reason as the invoice row in `Invoices.tsx`: this is a
                 whole card — a seven-column grid at `lg` — inside one button,
                 and the registry base's `inline-flex justify-center
                 whitespace-nowrap` plus its own height and padding would all
                 have to be undone before the columns lined up. */
              <button
                key={statement._id}
                type="button"
                onClick={() => setOpenId(statement._id)}
                className="group block w-full rounded-xl border bg-card text-left text-sm shadow-sm transition-colors hover:bg-muted/50"
              >
                {/* 390px: a card. Outstanding is the figure that decides
                    whether this month is finished, so it takes the weight;
                    the monthly total sits under it, and margin gets its
                    own labelled row below a hairline. */}
                <span className="flex items-center gap-3 p-4 lg:hidden">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">
                      {showClinic ? statement.clinicName : statement.number}
                    </span>
                    <span className="block text-muted-foreground">
                      {showClinic ? `${statement.number} · ` : ""}
                      {statement.invoiceCount}{" "}
                      {plural(statement.invoiceCount, "invoice")}
                    </span>
                    {/* The amount itself is the bold figure on the right,
                        so this says what kind of number it is rather than
                        repeating it. */}
                    {statement.outstandingCents > 0 ? (
                      <span className="mt-1 block text-xs font-semibold text-red-700">
                        still outstanding
                      </span>
                    ) : statement.monthlyTotalCents > 0 ? (
                      <span className="mt-1 block text-xs font-semibold text-green-700">
                        Fully paid
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-right">
                    {statement.outstandingCents > 0 ? (
                      <>
                        <span className="block text-lg font-semibold tabular-nums">
                          <Money cents={statement.outstandingCents} />
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          of <Money cents={statement.monthlyTotalCents} />
                        </span>
                      </>
                    ) : (
                      <span className="block text-lg font-semibold tabular-nums">
                        <Money cents={statement.monthlyTotalCents} />
                      </span>
                    )}
                    {statement.marginCents !== null &&
                    statement.monthlyTotalCents > 0 ? (
                      <span className="mt-1 block border-t pt-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                        margin <Money cents={statement.marginCents} />
                      </span>
                    ) : null}
                  </span>
                  <Chevron />
                </span>

                {/* lg: the same row, as a table row. */}
                <span className={cn("hidden px-4 py-3.5", columns)}>
                  {showClinic ? (
                    <span className="truncate font-semibold">
                      {statement.clinicName}
                    </span>
                  ) : null}
                  <span
                    className={cn(
                      showClinic ? "text-muted-foreground" : "truncate font-semibold",
                      "whitespace-nowrap tabular-nums",
                    )}
                  >
                    {statement.number}
                  </span>
                  <span className="text-right text-muted-foreground tabular-nums">
                    {statement.invoiceCount}
                  </span>
                  <span className="text-right text-muted-foreground tabular-nums">
                    <Money cents={statement.monthlyTotalCents} />
                  </span>
                  <span className="text-right font-semibold tabular-nums">
                    {statement.outstandingCents > 0 ? (
                      <span className="text-red-700">
                        <Money cents={statement.outstandingCents} />
                      </span>
                    ) : statement.monthlyTotalCents > 0 ? (
                      <span className="text-xs font-semibold text-green-700">
                        Fully paid
                      </span>
                    ) : (
                      <span className="font-normal text-muted-foreground">—</span>
                    )}
                  </span>
                  {showMargin ? (
                    <span className="text-right text-muted-foreground tabular-nums">
                      {statement.marginCents !== null &&
                      statement.monthlyTotalCents > 0 ? (
                        <Money cents={statement.marginCents} />
                      ) : (
                        <span>—</span>
                      )}
                    </span>
                  ) : null}
                  <Chevron />
                </span>
              </button>
            ),
          )}
        </section>
      )}
    </div>
  );
}

function StatementDetail({
  statementId,
  onClose,
}: {
  statementId: Id<"statements">;
  onClose: () => void;
}) {
  const data = useQuery(api.statements.get, { statementId });
  if (data === undefined) return <Loading rows={3} label="Loading statement" />;
  // Same reasoning as the invoice detail: a tapped row must not vanish silently.
  if (data === null) {
    return (
      <ErrorState
        title="That statement is no longer available"
        body="It may have been rebuilt by a later month end, or it belongs to another clinic."
        onRetry={onClose}
      />
    );
  }

  const { statement, invoices } = data;

  return (
    /* Registry card with the accent on its border — the same "this one is open"
       signal the expanded invoice carries, and the same `block gap-0 p-4` shim
       over the slots this card does not use. */
    <Card className="block gap-0 border-primary p-4 lg:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">{statement.clinicName}</div>
          <div className="text-sm text-muted-foreground">
            {statement.number} · {shortDate(statement.periodStart)} –{" "}
            {shortDate(statement.periodEnd - 1)}
          </div>
        </div>
        <Button variant="outline" className="shrink-0" onClick={onClose}>
          Close
        </Button>
      </div>

      {invoices.length === 0 ? (
        <p className="mt-4 rounded-md bg-muted p-3 text-sm text-muted-foreground">
          No supplies were invoiced to {statement.clinicName} in this period. A zero
          statement is still issued, so month end is never ambiguous.
        </p>
      ) : (
        /* The invoices a statement rolls up: number, status, total. Stock rows
           and stock hover, same as the invoice line table. Two layout classes
           survive on the identifier cell: `whitespace-normal`, so a long line
           at 390px wraps rather than pushing the money off the card, and
           `w-full`, which is what makes the two right-hand columns shrink to
           their content. */
        <Table className="mt-2">
          <TableCaption className="sr-only">
            The {invoices.length} {plural(invoices.length, "invoice")} in{" "}
            {statement.number}
          </TableCaption>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice._id}>
                <TableCell className="w-full whitespace-normal">
                  <div className="num font-medium">{invoice.number}</div>
                  <div className="text-xs text-muted-foreground">
                    {shortDate(invoice.issuedAt)} · {invoice.lineCount}{" "}
                    {plural(invoice.lineCount, "line")}
                  </div>
                </TableCell>
                <TableCell>
                  <StatusPill status={invoice.status} kind="invoice" />
                </TableCell>
                <TableCell className="num text-right font-semibold">
                  <Money cents={invoice.totalCents} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <dl className="mt-4 space-y-1 border-t pt-3 text-sm lg:ml-auto lg:w-80">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Supplies</dt>
          <dd><Money cents={statement.subtotalCents} /></dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Delivery &amp; admin</dt>
          <dd><Money cents={statement.feesTotalCents} /></dd>
        </div>
        <div className="flex justify-between border-t pt-1 text-lg font-semibold">
          <dt>Monthly total</dt>
          <dd><Money cents={statement.monthlyTotalCents} /></dd>
        </div>
        {statement.outstandingCents > 0 ? (
          <div className="flex justify-between font-semibold text-red-700">
            <dt>Outstanding</dt>
            <dd><Money cents={statement.outstandingCents} /></dd>
          </div>
        ) : null}
      </dl>
    </Card>
  );
}
